import React, { useState } from 'react';
import Grid from '@mui/material/Grid';
import { Button, Dialog, FormControlLabel } from '@mui/material';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import Checkbox from '@mui/material/Checkbox';
import { dateString, priceToString } from 'shared/utils';
import { BUTTON_COLORS, VARIANTS } from 'shared/constants';
import { formatBuildingUpgrade } from 'shared/utils/formatters';
import {
  applyCoupon,
  cancelSubscription,
  reactivateSubscription,
  updateSubscription,
} from 'shared/api/subscription';
import { applyVoucher } from 'shared/api/vouchers';
import { useAuth, useNotification, useRequest } from 'shared/hooks';
import { InfoModal } from 'shared/components/abstract/interactables';
import { TextAreaEditor, TextEditor } from 'shared/components/data-display/editors';
import { StaticSearchableSelect } from 'shared/components/select/SearchableSelects';
import Prorate from './Prorate';

/**
 * @param {Object} props
 * @param {Object} props.building - Building object
 * @param {number} props.currentPrice - Current subscription price
 * @param {string} props.newPlanId - New plan ID
 * @param {number} props.newPrice - New subscription price
 * @param {string} props.newQosDescription - New QOS description
 * @param {Function} props.reloadTenant - Function to reload tenant data
 * @param {number} props.tenantId - Tenant ID
 * @param {string} props.tenantName - Tenant name
 */
function ChangePlanModal(props) {
  const {
    building,
    currentPrice,
    newPlanId,
    newPrice,
    newQosDescription,
    reloadTenant,
    tenantId,
    tenantName,
  } = props;

  const { request, isLoading } = useRequest();
  const { pushError } = useNotification();

  const [show, setShow] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const onPlanChange = async () => {
    if (!building.unit) {
      pushError('Tenant is archived');
      return;
    }

    const result = await request(updateSubscription(tenantId, newPlanId), 'Updated plan');
    if (result) {
      reloadTenant();
      setShow(false);
    }
  };

  return (
    <>
      <Button
        onClick={() => setShow(true)}
        variant={VARIANTS.contained}
        color={BUTTON_COLORS.primary}
      >
        Update
      </Button>
      <Dialog
        open={show}
        size="md"
      >
        <DialogTitle>
          {`Change Plan to ${newQosDescription}`}
        </DialogTitle>
        <DialogContent>
          <Prorate
            currentPrice={currentPrice}
            newPlanId={newPlanId}
            newPrice={newPrice}
            tenantId={tenantId}
          />
          <FormControlLabel
            control={(
              <Checkbox
                checked={confirmed}
                onChange={() => setConfirmed(!confirmed)}
                slotProps={{ input: { 'aria-label': 'controlled' } }}
              />
            )}
            label={`${tenantName} accepts billing change`}
          />
        </DialogContent>
        <DialogActions className="flex-between">
          <Button
            variant={VARIANTS.outlined}
            color={BUTTON_COLORS.primary}
            onClick={() => setShow(false)}
          >
            Close
          </Button>
          {(confirmed) && (
            <Button
              disabled={isLoading}
              color={BUTTON_COLORS.primary}
              variant={VARIANTS.contained}
              onClick={onPlanChange}
            >
              Confirm
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}

/**
 * @param {Object} props
 * @param {boolean} props.isVoucherService - Whether this is a voucher service
 * @param {number} props.periodEnd - Period end timestamp
 * @param {number} props.price - Subscription price
 * @param {string} props.qosDescription - QOS description
 * @param {Function} props.reloadTenant - Function to reload tenant data
 * @param {number} props.tenantId - Tenant ID
 */
function CancelSubscriptionModal(props) {
  const {
    isVoucherService,
    periodEnd,
    price,
    qosDescription,
    reloadTenant,
    tenantId,
  } = props;

  const { user } = useAuth();
  const {
    request,
    isLoading,
  } = useRequest();
  const [show, setShow] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [cancelReason, setCancelReason] = useState('');

  const cancelImmediately = 'cancel immediately';

  const confirmCancel = async (cancelNow) => {
    const result = await request(cancelSubscription(
      tenantId,
      cancelNow,
      cancelReason,
      user.username,
    ), `Canceled ${isVoucherService ? 'voucher' : 'subscription'}`);
    if (result) {
      setShow(false);
      reloadTenant();
    }
  };

  const confirmTextMatches = confirmText === cancelImmediately;
  let buttonText;
  if (isVoucherService) {
    buttonText = 'End Voucher Now';
  } else if (confirmTextMatches) {
    buttonText = 'Cancel Immediately';
  } else {
    buttonText = `Cancel on ${dateString(periodEnd)}`;
  }

  return (
    <>
      <Button
        onClick={() => setShow(true)}
        variant={VARIANTS.contained}
        color={BUTTON_COLORS.error}
      >
        Cancel
        {` ${isVoucherService ? 'Voucher' : 'Subscription'}`}
      </Button>
      <Dialog
        open={show}
        size="md"
      >
        <DialogTitle>
          Cancel
          {isVoucherService ? ` ${qosDescription} Voucher` : ` ${qosDescription} for ${priceToString(price)}`}
        </DialogTitle>
        <DialogContent>
          {isVoucherService
            ? (
              <p>
                This voucher will automatically end on
                <strong>
                  {` ${dateString(periodEnd)}`}
                </strong>
                .
              </p>
            ) : (
              <>
                <p>
                  This subscription can be canceled immediately or at the current period&apos;s end,
                  <strong>
                    {` ${dateString(periodEnd)}`}
                  </strong>
                  .
                </p>
                <p>
                  Canceling immediately will
                  <strong>{' not issue a refund for unused time '}</strong>
                  and
                  <strong>{' cannot be undone '}</strong>
                  .
                  If needed, type
                  {` "${cancelImmediately}".`}
                </p>
                <TextEditor
                  label="Cancel Immediately?"
                  setValue={(value) => setConfirmText(value)}
                  value={confirmText}
                />
                <br />
              </>
            )}
          <TextAreaEditor
            label="Cancel Reason"
            as="textarea"
            setValue={(value) => setCancelReason(value)}
            value={cancelReason}
          />
        </DialogContent>
        <DialogActions className="flex-between">
          <Button
            variant={VARIANTS.outlined}
            color={BUTTON_COLORS.primary}
            onClick={() => setShow(false)}
          >
            Close
          </Button>
          <Button
            disabled={isLoading}
            variant={VARIANTS.contained}
            color={BUTTON_COLORS.error}
            onClick={() => confirmCancel(isVoucherService || confirmText === cancelImmediately)}
          >
            {buttonText}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/**
 * @param {Object} props
 * @param {Object} props.building - Building object
 * @param {Array<Object>} props.buildingUpgrades - Array of available building upgrades
 * @param {Function} props.reloadTenant - Function to reload tenant data
 * @param {Object} props.subscription - Subscription object
 * @param {number} props.tenantId - Tenant ID
 * @param {string} props.tenantName - Tenant name
 */
export default function SubscriptionManager(props) {
  const {
    building,
    buildingUpgrades,
    reloadTenant,
    subscription,
    tenantId,
    tenantName,
  } = props;

  const {
    request,
    isLoading,
  } = useRequest();
  const hasSubscription = !!subscription.id;

  const [newUpgrade, setNewUpgrade] = useState(undefined);
  const [newCouponId, setNewCouponId] = useState('');
  const [newVoucher, setNewVoucher] = useState('');

  const onReactivateSubscription = async () => {
    const result = await request(reactivateSubscription(tenantId));
    if (result) {
      reloadTenant();
    }
  };

  const onApplyCoupon = async () => {
    const result = await request(applyCoupon(tenantId, newCouponId), 'Applied coupon');
    if (result) {
      reloadTenant();
    }
  };

  const onApplyVoucher = async () => {
    const result = await request(applyVoucher(tenantId, newVoucher), 'Applied voucher');
    if (result) {
      reloadTenant();
    }
  };

  const changeValue = (value) => {
    setNewUpgrade(value);
  };

  return (
    <Grid container direction="column" spacing={1.5} sx={{ width: '100%' }}>
      <Grid container spacing={2}>
        <Grid size={10}>
          <TextEditor
            setValue={(value) => setNewVoucher(value)}
            value={newVoucher}
            label="Apply Voucher"
          />
        </Grid>
        <Grid
          size={2}
          sx={{
            alignContent: 'center',
          }}
        >
          <Button
            disabled={isLoading}
            onClick={onApplyVoucher}
            color={BUTTON_COLORS.primary}
            variant={VARIANTS.contained}
          >
            Submit
          </Button>
        </Grid>
      </Grid>
      {hasSubscription && (
        <>
          {!subscription.isVoucherService && (
            <>
              <Grid
                container
                spacing={2}
                sx={{
                  justifyContent: 'space-between',
                }}
              >
                <Grid size={10}>
                  <TextEditor
                    label="Apply Coupon"
                    setValue={(value) => setNewCouponId(value)}
                    value={newCouponId}
                    type="text"
                  />
                </Grid>
                <Grid
                  size={2}
                  sx={{
                    alignContent: 'center',
                  }}
                >
                  <Button
                    disabled={isLoading || newCouponId === subscription.couponId}
                    onClick={onApplyCoupon}
                    color={BUTTON_COLORS.primary}
                    variant={VARIANTS.contained}
                  >
                    Submit
                  </Button>
                </Grid>
              </Grid>
              <Grid container spacing={2}>

                <Grid size={10}>
                  <StaticSearchableSelect
                    label="Change Plan"
                    onChange={changeValue}
                    getOptionLabel={(option) => formatBuildingUpgrade(option)}
                    staticOptions={buildingUpgrades.map((u) => ({
                      ...u,
                      disabled: u.plan.id === subscription.planId,
                    }))}
                    selectedOption={newUpgrade}
                  />
                </Grid>
                <Grid sx={{
                  alignContent: 'center',
                }}
                >
                  {newUpgrade && newUpgrade?.id !== subscription.planId && (
                    <ChangePlanModal
                      building={building}
                      currentPrice={subscription.price || 0}
                      newPlanId={newUpgrade.plan.id}
                      newPrice={newUpgrade.plan.price}
                      newQosDescription={newUpgrade.plan.product.qos.description}
                      reloadTenant={reloadTenant}
                      tenantId={tenantId}
                      tenantName={tenantName}
                    />
                  )}
                </Grid>
              </Grid>
            </>
          )}
          <Grid
            container
            size={12}
            sx={{
              alignContent: 'center',
            }}
          >
            <>
              {((!subscription.cancelAtPeriodEnd || subscription.isVoucherService)) && (
                <CancelSubscriptionModal
                  isVoucherService={subscription.isVoucherService}
                  periodEnd={subscription.periodEnd || 0}
                  price={subscription.price || 0}
                  qosDescription={subscription.description || ''}
                  reloadTenant={reloadTenant}
                  tenantId={tenantId}
                />
              )}
              {(subscription.cancelAtPeriodEnd && !subscription.isVoucherService)
                && (
                  <InfoModal
                    actionCallback={onReactivateSubscription}
                    actionName="Reactivate"
                    closeText="Close"
                    description={`Reactivating the subscription will resume billing on ${dateString(subscription.periodEnd)}.`}
                    title="Reactivate Subscription"
                    color={BUTTON_COLORS.primary}
                  />
                )}
            </>
          </Grid>
        </>
      )}
    </Grid>
  );
}
